import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import AgentDetailDesktopView from 'components/AgentDetailDesktopView';
import AgentDetailSheet from 'components/AgentDetailSheet';
import { useMediaQuery } from 'hooks/useMediaQuery';
import { findActionIn } from 'actions';
import { useGetAgent } from 'features/agents/useGetAgent';
import { useEditAgent } from 'features/agents/useEditAgent';
import { useResetAgentPassword } from 'features/agents/useResetAgentPassword';
import { useRemoveAgent } from 'features/agents/useRemoveAgent';
import { agentIdOf, agentPrimaryEmail, agentStatusId, } from 'features/agents/types';
function toWorkspaceAgent(agent, fallbackId) {
    return {
        id: agentIdOf(agent) || fallbackId,
        name: agent.name || '',
        summary: '',
        projectIds: [],
        email: agentPrimaryEmail(agent),
        phone: agent.whatsAppE164 || '',
        status: agentStatusId(agent),
        notes: agent.adminNotes || '',
    };
}
export default function AgentDetailWorkspace({ agentId }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isDesktop = useMediaQuery('(min-width: 1024px)');
    const query = useGetAgent({ agentId });
    const editAgent = useEditAgent();
    const resetAgentPassword = useResetAgentPassword();
    const removeAgent = useRemoveAgent();
    const agent = useMemo(() => {
        if (!query.data)
            return null;
        return toWorkspaceAgent(query.data, agentId);
    }, [query.data, agentId]);
    const goBack = () => {
        void navigate({ to: '/agents' });
    };
    const handleSave = (next) => {
        if (!query.data)
            return;
        // Form joins phone as `+CC-NNN`; backend expects E.164 (`+CCNNN`).
        const phoneE164 = (next.phone || '').replace(/-/g, '');
        editAgent.mutate({
            agent: query.data,
            edits: {
                name: next.name,
                whatsAppE164: phoneE164 || undefined,
                status: next.status,
                adminNotes: next.notes ?? '',
            },
        });
    };
    const handleResetPassword = () => {
        if (!query.data)
            return;
        resetAgentPassword.mutate({ agent: query.data });
    };
    const handleDelete = () => {
        if (!query.data)
            return;
        removeAgent.mutate({ agent: query.data }, { onSuccess: () => goBack() });
    };
    if (query.isPending) {
        return (_jsx("p", { className: "m-0 px-2 py-6 text-center text-[14px] leading-5 text-blacktext-080", children: t('agents_loading') }));
    }
    if (query.isError || !agent) {
        return (_jsx("p", { className: "m-0 px-2 py-6 text-center text-[14px] leading-5 text-error", children: t('agents_load_error') }));
    }
    // Per-record action availability — only render the corresponding button when the
    // backend actually exposed the action on this agent's potentialAction map.
    const canEdit = Boolean(findActionIn(query.data?.potentialAction, 'edit'));
    const canResetPassword = Boolean(findActionIn(query.data?.potentialAction, 'resetPassword'));
    const canRemove = Boolean(findActionIn(query.data?.potentialAction, 'remove'));
    if (isDesktop) {
        return (_jsx(AgentDetailDesktopView, { agent: agent, rawAgent: query.data, onBack: goBack, onSave: handleSave, canEdit: canEdit, onDelete: handleDelete, isDeleting: removeAgent.isPending, canRemove: canRemove, onResetPassword: handleResetPassword, canResetPassword: canResetPassword }));
    }
    return (_jsx(AgentDetailSheet, { open: true, agent: agent, rawAgent: query.data, onOpenChange: (next) => {
            if (!next)
                goBack();
        }, onSave: handleSave, canEdit: canEdit, onDelete: handleDelete, isDeleting: removeAgent.isPending, canRemove: canRemove, onResetPassword: handleResetPassword, canResetPassword: canResetPassword }));
}
